import type { ToastServiceMethods } from "primevue/toastservice"

import { apolloClient } from "../queries/client"
import CANCEL_ORDER from "../queries/order/cancel.gql"
import CANCEL_PUSH from "../queries/order/cancelPush.gql"
import CREATE_ORDER from "../queries/order/create.gql"
import DESTROY_ORDER from "../queries/order/destroy.gql"
import PUSH_DOCUMENTS from "../queries/order/pushDocuments.gql"

export interface MutationResult<T = void> {
  success: boolean
  errors?: string[]
  data?: T
}

export function useOrderMutations(toast: ToastServiceMethods) {
  const createOrder = async (
    variables: Record<string, unknown>,
  ): Promise<MutationResult<{ gid: string }>> => {
    try {
      const result = await apolloClient.mutate({
        mutation: CREATE_ORDER,
        variables,
      })

      if (result?.data?.createOrder?.errors?.length) {
        const errors = result.data.createOrder.errors
        toast.add({
          severity: "error",
          summary: "Error",
          detail: errors.join(", "),
          life: 5000,
        })
        return { success: false, errors }
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Order created successfully",
        life: 4000,
      })

      return { success: true, data: { gid: result.data.createOrder.order.gid } }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to create order",
        life: 5000,
      })
      return { success: false }
    }
  }

  const cancelOrder = async (orderId: string): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: CANCEL_ORDER,
        variables: { orderId },
      })

      if (result?.data?.cancelOrder?.errors?.length) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.data.cancelOrder.errors.join(", "),
          life: 4000,
        })
        return { success: false, errors: result.data.cancelOrder.errors }
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Closing cancelled. Order returned to draft.",
        life: 4000,
      })

      return { success: true }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to cancel closing",
        life: 4000,
      })
      return { success: false }
    }
  }

  const destroyOrder = async (orderId: string): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: DESTROY_ORDER,
        variables: { orderId },
      })

      if (result?.data?.destroyOrder?.success) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Order deleted",
          life: 4000,
        })
        return { success: true }
      } else {
        const errors = result?.data?.destroyOrder?.errors?.length
          ? result.data.destroyOrder.errors
          : ["Unknown error"]
        toast.add({
          severity: "error",
          summary: "Error",
          detail: errors.join(", "),
          life: 5000,
        })
        return { success: false, errors }
      }
    } catch (error) {
      console.error("Mutation error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete order",
        life: 5000,
      })
      return { success: false }
    }
  }

  const pushDocuments = async (orderId: string): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: PUSH_DOCUMENTS,
        variables: { orderId },
      })

      if (result?.data?.pushDocuments?.errors?.length) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.data.pushDocuments.errors.join(", "),
          life: 4000,
        })
        return { success: false, errors: result.data.pushDocuments.errors }
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Push started.",
        life: 4000,
      })

      return { success: true }
    } catch (error) {
      console.error("Push error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to start push",
        life: 4000,
      })
      return { success: false }
    }
  }

  const cancelPush = async (orderId: string): Promise<MutationResult> => {
    try {
      const result = await apolloClient.mutate({
        mutation: CANCEL_PUSH,
        variables: { orderId },
      })

      if (result?.data?.cancelPush?.errors?.length) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.data.cancelPush.errors.join(", "),
          life: 4000,
        })
        return { success: false, errors: result.data.cancelPush.errors }
      }

      toast.add({
        severity: "info",
        summary: "Cancelled",
        detail: "Push cancelled.",
        life: 4000,
      })

      return { success: true }
    } catch (error) {
      console.error("Cancel push error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to cancel push",
        life: 4000,
      })
      return { success: false }
    }
  }

  return { createOrder, cancelOrder, destroyOrder, pushDocuments, cancelPush }
}
